import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { PropertiesService } from '@core/services/properties.service';
import { UserService } from '@core/services/user.service';

import { PlaygroundCompanyRoleEnum } from '@shared/enums/playground-company-role.enum';
import { validateFormGroup } from '@shared/utils/form.util';

import { AuthenticationService } from '@core/services/authentication.service';
import { StorageService } from '@core/services/storage.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SignUpTypeEnum } from '@shared/enums/sign-up-type.enum';

@UntilDestroy()
@Component({
  selector: 'app-playground-register',
  templateUrl: './playground-register.component.html'
})
export class PlaygroundRegisterComponent implements OnInit {

  // Google OAuth
  gapiSetup = false;
  authInstance: gapi.auth2.GoogleAuth;
  error: string;
  user: gapi.auth2.GoogleUser;

  registerForm: FormGroup;
  loading = false;
  companyRoles = PlaygroundCompanyRoleEnum;
  signUpTypeEnum = SignUpTypeEnum;
  companyRolesOptions = [];

  constructor(public propertiesService: PropertiesService,
              private userService: UserService,
              private storageService: StorageService,
              private authenticationService: AuthenticationService,
              private fb: FormBuilder) { }

  ngOnInit(): void {
    this.companyRolesOptions = Object.keys(this.companyRoles);
    this.createForm();
  }

  createForm(): void {
    this.registerForm = this.fb.group({
      name: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.email]],
    });
  }

  async initGoogleAuth(): Promise<void> {
    const pload = new Promise((resolve) => gapi.load('auth2', resolve));

    return pload.then(async () => {
      await gapi.auth2
        .init({ client_id: this.propertiesService.GOOGLE_AUTH_URL })
        .then(auth => {
          this.gapiSetup = true;
          this.authInstance = auth;
        });
    });
  }

  signUp(type: string): void {
    this.storageService.set('AUTH_TYPE', type);
    switch (type) {
      case this.signUpTypeEnum.EMAIL: {
        this.signUpWithEmail();
        break;
      }
      case this.signUpTypeEnum.GOOGLE: {
        this.authenticate();
        break;
      }
      case this.signUpTypeEnum.GITHUB: {
        window.location.href = this.propertiesService.GITHUB_AUTH_URL;
        break;
      }
      case this.signUpTypeEnum.LINKEDIN: {
        window.location.href = this.propertiesService.LINKEDIN_AUTH_URL;
        break;
      }
    }
  }

  async authenticate(): Promise<gapi.auth2.GoogleUser> {
    if (!this.gapiSetup) {
      await this.initGoogleAuth();
    }

    return new Promise(async () => {
      await this.authInstance.signIn().then(
        user => {
          const userProfile = user.getBasicProfile();
          const data = {
            name: userProfile.getGivenName(),
            email: userProfile.getEmail(),
          };
          this.createUserPlayground(data);
        },
        error => {
          this.error = error;
          console.log('error : ', error);
        });
    });
  }

  signUpWithEmail(): void {
    if (this.loading) {
      return;
    }

    validateFormGroup(this.registerForm);

    if (this.registerForm.invalid) {
      return;
    }

    this.loading = true;

    this.createUserPlayground(this.registerForm.value);
  }

  createUserPlayground(data: User.PlaygroundRegisterModel) {
    this.userService.createUserPlayground(data)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.authenticationService
          .login(this.propertiesService.demoCredentials)
          .subscribe(() => this.loading = false);
      }, error => {
        this.loading = false;
        console.log('error while sign up', error);
      });
  }
}
